/**
 * The action bar for a claim.
 *
 * Same shape as the proposal's, and for the same reason: `available_actions` comes from the
 * table the API enforces, so a button cannot render and then 403. The one difference is that two
 * of these actions move money, and they carry an **idempotency key** generated when the dialog
 * opens — so a double-tapped Approve on a phone replays the first answer instead of settling
 * twice, and a retry after a dropped connection is safe.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, ApiError, idempotencyKey } from '@/lib/api';
import { ACTION_LABEL } from '@/lib/policy';
import { Alert, Button, Dialog, Field, Input, Textarea } from '@/components/ui';
import { ErrorNote, Money } from '@/components/bits';
import { sgtToday } from '@/lib/dates';
import type { Claim, ClaimAction } from '@/lib/types';

interface Spec {
  prompt?: string;
  field?: string;
  required?: boolean;
  variant?: 'primary' | 'secondary' | 'destructive';
  /** A second input, for `mark_paid`'s payment reference. */
  extra?: { label: string; placeholder: string; required: boolean };
  run: (claim: Claim, note: string, extra: string, key: string) => Promise<unknown>;
}

const SPECS: Partial<Record<ClaimAction, Spec>> = {
  sponsor_endorse: {
    prompt: 'Endorsing sends this to HR to check the figures and the evidence.',
    field: 'Note (optional)',
    variant: 'primary',
    run: (claim, note) =>
      api.claimSponsorAction(claim.id, {
        action: 'endorse',
        ...(note.trim() ? { note: note.trim() } : {}),
        expected_version: claim.version,
      }),
  },
  sponsor_return: {
    prompt: 'The submitter sees this reason and can fix it and resubmit.',
    field: 'What needs changing',
    required: true,
    run: (claim, note) =>
      api.claimSponsorAction(claim.id, {
        action: 'return',
        note: note.trim(),
        expected_version: claim.version,
      }),
  },
  sponsor_reject: {
    prompt: 'Rejecting ends this claim. Returning it is usually kinder.',
    field: 'Reason',
    required: true,
    variant: 'destructive',
    run: (claim, note) =>
      api.claimSponsorAction(claim.id, {
        action: 'reject',
        note: note.trim(),
        expected_version: claim.version,
      }),
  },
  hr_verify: {
    prompt:
      'Confirm you have opened the submitter’s Drive folder, copied the files into the org Shared Drive, and pasted the archive link (D-22). A link that does not open is a return, not an approval.',
    field: 'Note (optional)',
    variant: 'primary',
    run: (claim, note) =>
      api.verifyClaim(claim.id, {
        action: 'verify',
        ...(note.trim() ? { note: note.trim() } : {}),
        expected_version: claim.version,
      }),
  },
  hr_return: {
    prompt: 'The submitter sees this reason.',
    field: 'What needs fixing',
    required: true,
    run: (claim, note) =>
      api.verifyClaim(claim.id, {
        action: 'return',
        note: note.trim(),
        expected_version: claim.version,
      }),
  },
  hr_reject: {
    prompt: 'Rejecting ends this claim.',
    field: 'Reason',
    required: true,
    variant: 'destructive',
    run: (claim, note) =>
      api.verifyClaim(claim.id, {
        action: 'reject',
        note: note.trim(),
        expected_version: claim.version,
      }),
  },
  director_return: {
    prompt: 'Sent back to the submitter with your reason.',
    field: 'What needs fixing',
    required: true,
    run: (claim, note, _extra, key) =>
      api.approveClaim(
        claim.id,
        { action: 'return', note: note.trim(), expected_version: claim.version },
        key,
      ),
  },
  director_reject: {
    prompt: 'Rejecting ends this claim. Nothing is settled and the reservation stays as it is.',
    field: 'Reason',
    required: true,
    variant: 'destructive',
    run: (claim, note, _extra, key) =>
      api.approveClaim(
        claim.id,
        { action: 'reject', note: note.trim(), expected_version: claim.version },
        key,
      ),
  },
  request_clarification: {
    prompt: 'This parks the claim with whoever must answer and returns it to you when they do.',
    field: 'Your question',
    required: true,
    run: (claim, note) =>
      api.askClaimClarification(claim.id, {
        body: note.trim(),
        expected_version: claim.version,
      }),
  },
  mark_paid: {
    prompt:
      'Recording the reference is what makes “where is my reimbursement?” answerable without asking HR (D-29).',
    field: 'Note (optional)',
    variant: 'primary',
    extra: {
      label: 'Payment reference',
      placeholder: 'GIRO batch, cheque number…',
      required: true,
    },
    run: (claim, note, extra, key) =>
      api.markClaimPaid(
        claim.id,
        {
          paid_at: sgtToday(),
          payment_reference: extra.trim(),
          ...(note.trim() ? { note: note.trim() } : {}),
          expected_version: claim.version,
        },
        key,
      ),
  },
};

/** Handled elsewhere: submitting is the form's job, and approving has its own screen. */
const ELSEWHERE: ClaimAction[] = ['submit', 'answer_clarification', 'director_approve'];

export function ClaimActions({ claim, onApprove }: { claim: Claim; onApprove?: () => void }) {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<ClaimAction | null>(null);
  const [note, setNote] = useState('');
  const [extra, setExtra] = useState('');
  // Generated once per opened dialog: a retry of *this* attempt replays, a fresh attempt does not.
  const [key, setKey] = useState(() => idempotencyKey());

  const spec = open ? SPECS[open] : undefined;

  const act = useMutation({
    mutationFn: async () => {
      if (!open || !spec) throw new Error('no action selected');
      return spec.run(claim, note, extra, key);
    },
    onSuccess: async () => {
      setOpen(null);
      setNote('');
      setExtra('');
      await cache.invalidateQueries({ queryKey: ['claim', claim.id] });
      await cache.invalidateQueries({ queryKey: ['claims'] });
      await cache.invalidateQueries({ queryKey: ['settlement', claim.id] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
      await cache.invalidateQueries({ queryKey: ['proposal', claim.proposal.id] });
    },
  });

  const buttons = claim.available_actions.filter((action) => !ELSEWHERE.includes(action));
  const canEdit = claim.available_actions.includes('submit');
  const canApprove = claim.available_actions.includes('director_approve');

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {canEdit ? (
          <Button variant="primary" onClick={() => navigate(`/claims/${claim.id}/edit`)}>
            Edit and submit
          </Button>
        ) : null}
        {canApprove && onApprove ? (
          <Button variant="primary" onClick={onApprove}>
            {ACTION_LABEL.director_approve}
          </Button>
        ) : null}
        {buttons.map((action) => (
          <Button
            key={action}
            variant={SPECS[action]?.variant === 'destructive' ? 'destructive' : 'secondary'}
            onClick={() => {
              setNote('');
              setExtra('');
              setKey(idempotencyKey());
              setOpen(action);
            }}
          >
            {ACTION_LABEL[action] ?? action}
          </Button>
        ))}
      </div>

      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? (ACTION_LABEL[open] ?? open) : ''}
        footer={
          <>
            <Button onClick={() => setOpen(null)}>Cancel</Button>
            <Button
              variant={spec?.variant === 'destructive' ? 'destructive' : 'primary'}
              busy={act.isPending}
              disabled={
                (spec?.required === true && note.trim() === '') ||
                (spec?.extra?.required === true && extra.trim() === '')
              }
              onClick={() => act.mutate()}
            >
              {open ? (ACTION_LABEL[open] ?? 'Confirm') : 'Confirm'}
            </Button>
          </>
        }
      >
        {spec?.prompt ? <p className="text-muted-foreground">{spec.prompt}</p> : null}

        {open === 'mark_paid' ? (
          <p className="text-sm">
            Reimbursing <Money cents={claim.settlement.reimbursable_cents} /> for{' '}
            {claim.ref_no ?? 'this claim'}.
          </p>
        ) : null}

        {spec?.extra ? (
          <Field label={spec.extra.label} required={spec.extra.required}>
            {(props) => (
              <Input
                {...props}
                value={extra}
                placeholder={spec.extra?.placeholder}
                onChange={(event) => setExtra(event.target.value)}
              />
            )}
          </Field>
        ) : null}

        {spec?.field ? (
          <Field label={spec.field} required={spec.required}>
            {(props) => (
              <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />
            )}
          </Field>
        ) : null}

        {act.error instanceof ApiError && act.error.isStale ? (
          <Alert tone="warn" title="This claim changed while you were looking at it">
            Someone else acted on it. Reload and check what changed before acting.
          </Alert>
        ) : act.error ? (
          <ErrorNote error={act.error} />
        ) : null}
      </Dialog>
    </>
  );
}
